import { useState, type FC } from 'react';
import { ProductAttributeInterface } from '../../../../../../Interface/ProductAttribute';
import { User, useUser } from '../../../../../Context/UserContext';
import useViewports from '../../../../../Hooks/useViewports';
import ProductAttributeDesktop from '../ProductAttributeDesktop/ProductAttributeDesktop';
import ProductAttributeMobile from '../ProductAttributeMobile/ProductAttributeMobile';
import ProductSizeGallery from '../../../../ProductSize/ProductSizeGallery/ProductSizeGallery';
import ProductQuickViewModal from './components/ProductQuickViewModal/ProductQuickViewModal';
import { OnlineshopProductImages } from '../../../../../../Interface/OnlineshopProductImages';
import axios from 'axios';

export type ProductAttributeDisplayPropsT = {
  attribute: ProductAttributeInterface;
  isClearance: boolean;
  user: User;
  getAttributeImages: (id: number) => void;
  onShowQuickView: (id: number) => void;
  allowBackorder?: boolean;
};

type ProductAttributePropsT = {
  attribute: ProductAttributeInterface;
  allowBackorder?: boolean;
};

const emptyImages: OnlineshopProductImages = {
  front_image_url: '',
  nutrition_facts_image_url: '',
  sell_sheets: [],
};

const ProductAttribute: FC<ProductAttributePropsT> = ({
  attribute,
  allowBackorder = false,
}) => {
  const { xs, sm, md } = useViewports();

  const { user } = useUser();
  // P-4877 (Punkt 1): Clearance-Markierung wird unabhängig vom Login angezeigt.
  // Der reduzierte Preis bleibt eingeloggten Nutzern vorbehalten (weiter unten).
  const isClearance =
    !!attribute.combined_description_1 &&
    (attribute.clearance_discount_percent ?? 0) > 0;

  const [attributeImages, setAttributeImages] =
    useState<OnlineshopProductImages>(emptyImages);

  const [showImageGalerie, setShowImageGalerie] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);

  function fetchAndShowImages(
    id: number,
    target: 'gallery' | 'quickview'
  ) {
    axios
      .get(`/api/onlineshop/product-attributes/${id}/product-images`)
      .then((resp) => {
        setAttributeImages(resp.data);
        if (target === 'gallery') {
          setShowImageGalerie(true);
        } else {
          setShowQuickView(true);
        }
      })
      .catch(() => {});
  }

  function getAttributeImages(id: number) {
    fetchAndShowImages(id, 'gallery');
  }

  function openQuickView(id: number) {
    fetchAndShowImages(id, 'quickview');
  }

  return (
    <>
      {xs || sm || md ? (
        <ProductAttributeMobile
          attribute={attribute}
          user={user}
          isClearance={isClearance}
          getAttributeImages={getAttributeImages}
          onShowQuickView={openQuickView}
          allowBackorder={allowBackorder}
        />
      ) : (
        <ProductAttributeDesktop
          attribute={attribute}
          isClearance={isClearance}
          user={user}
          getAttributeImages={getAttributeImages}
          onShowQuickView={openQuickView}
          allowBackorder={allowBackorder}
        />
      )}

      <ProductSizeGallery
        showImageGalerie={showImageGalerie}
        setShowImageGalerie={setShowImageGalerie}
        images={attributeImages}
      />

      <ProductQuickViewModal
        show={showQuickView}
        onHide={() => setShowQuickView(false)}
        attribute={attribute}
        images={attributeImages}
      />
    </>
  );
};

export default ProductAttribute;
